import {
  useLocation,
} from "react-router-dom";
import { TAB_MANAGEMENT_REPORT, TAB_MANAGEMENT_REPORT_COUNT } from "./fragment";
import { ReportFilters } from "../../components/ReportFilters";
import ReportHeader from "../../components/ReportHeader";
import { userStore } from "../../../../store/user";
import { createApolloClient } from "../../../../providers/ApolloClientFactory";
import { organizationStore } from "../../../../store/organization";
import { TabManagementReportRow } from "./type";
import React, {useEffect, useRef, useState} from "react";
import { eventStore, useEventStore } from "../../../../store/event";
import { useOrganizationStore } from "../../../../store/organization";
import { useAccumulatedOptions, useLocationFilterOptions } from "../../components/useFilterOptions";
import { CsvCell, GridApi, SizeColumnsToContentStrategy } from "ag-grid-community";
import { buildReportCsvHeader } from "../../utils/buildReportCsvHeader";
import { SecondaryButton } from "../../../../components/Button";
import { AgGridReact } from "ag-grid-react";
import { colDefs } from "./helpers";
import Loader from "../../../../components/Loader";
import CustomSelect from "../../../../components/customSelect";

const client = createApolloClient();
const { getState } = userStore;
const { getState: getOrganizationState } = organizationStore;
const { getState: getEventState } = eventStore;
const PAGE_SIZE = 50000;
const DEBOUNCE_DELAY = 100;

const tabStatusOptions = [
  { value: "all", label: "All" },
  { value: "open_tab", label: "Open Tab" },
  { value: "processed", label: "Closed Tab / Processed" },
  { value: "deleted_tab", label: "Deleted Tab" },
  { value: "in_use", label: "In Use / Locked" },
];

export const TabManagementReport = () => {
  const [data, setData] = useState<TabManagementReportRow[]>([]);
  const filterEventId = useEventStore((state) => state.eventId);
  const filterOrganizationId = useOrganizationStore((state) => state.organizationId);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const gridRef = useRef<GridApi>();
  const rowHeight = 50;
  const autoSizeStrategy: SizeColumnsToContentStrategy = { type: "fitCellContents" };
  const location = useLocation();
  const fetchTimeoutRef = useRef<any>(null);

  // === Derived options (no refs; recompute when `data` changes) ===
  // Usernames: every value seen for this event, not only the current rows,
  // so a second pick is possible while the first filters the rows.
  const usernameOptions = useAccumulatedOptions(
      data,
      (r) => r.user?.username,
      `${filterOrganizationId ?? ""}:${filterEventId ?? ""}`
  );

  // Locations: the event's active locations (the Vendor dropdown's source),
  // not the rows, which are already filtered by location.
  const locationOptions = useLocationFilterOptions();

  const handleExportCSV = () => {
    if (gridRef.current) {
      const timestamp = new Date().toISOString().replace(/[-:]/g, "");
      const params = {
        fileName: `TabManagementReport_${timestamp}.csv`,
        columnSeparator: ",",
        skipHeader: false,
        allColumns: true,
        prependContent: buildReportCsvHeader({
          reportName: "Tab Management Report",
          searchString: location.search,
        }),
      };
      gridRef.current.exportDataAsCsv(params);
    }
  };

  useEffect(() => {
    let isMounted = true;

    const fetchTransactions = async () => {
      setIsLoading(true);
      try {
        const rawOrganizationId = getOrganizationState().organizationId;
        const organizationId = parseInt(
            rawOrganizationId !== null ? rawOrganizationId.toString() : "",
            10
        );
        const { eventId, allEvents } = getEventState();
        const user = getState().user;

        const search = new URLSearchParams(location.search);
        const where = search.get("where");
        // RP-1937: no filter in the address yet; ReportFilters re-applies it and this runs again.
        if (!where) return;
        let whereVariable: any = where ? JSON.parse(where) : {};

        // Remove fields that don't exist on the orders table
        delete whereVariable.organization_id;
        delete whereVariable.payment_status;
        delete whereVariable.transaction_hour;

        if (user?.vendors && !whereVariable.vendor_id) {
          whereVariable = {
            ...whereVariable,
            vendor_id: { _in: user.vendors },
          };
        }

        if (eventId) {
          whereVariable = {
            ...whereVariable,
            event_id: { _eq: eventId },
          };
        } else if (allEvents?.length) {
          // No specific event selected — use all events for the selected org
          const orgEventIds = allEvents.map((e: any) => e.id);
          whereVariable = {
            ...whereVariable,
            event_id: { _in: orgEventIds },
          };
        } else if (user?.events?.length) {
          whereVariable = {
            ...whereVariable,
            event_id: { _in: user.events },
          };
        }

        whereVariable = {
          ...whereVariable,
          is_tab: { _eq: true },
        };

        if (statusFilter !== "all") {
          whereVariable = {
            ...whereVariable,
            status: { _eq: statusFilter },
          };
        }

        const { data: totalData } = await client.query({
          query: TAB_MANAGEMENT_REPORT_COUNT,
          variables: { where: whereVariable, limit: 0 },
        });

        const totalCount = totalData.total.aggregate.count as number;
        let offset = 0;
        let chunkData: TabManagementReportRow[] = [];

        while (offset < totalCount) {
          const { data: chunk } = await client.query({
            query: TAB_MANAGEMENT_REPORT,
            variables: {
              where: whereVariable,
              limit: PAGE_SIZE,
              offset,
            },
          });
          chunkData = [...chunkData, ...(chunk.orders || [])];
          offset += PAGE_SIZE;
        }

        if (isMounted) {
          setData(chunkData);
        }
      } catch (error) {
        console.error("Error fetching tab management data:", error);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    const debouncedFetch = () => {
      clearTimeout(fetchTimeoutRef.current);
      fetchTimeoutRef.current = setTimeout(fetchTransactions, DEBOUNCE_DELAY);
    };

    debouncedFetch();

    return () => {
      isMounted = false;
      clearTimeout(fetchTimeoutRef.current);
    };
  }, [location.search, statusFilter]);

  const gridOptions = { enableCellTextSelection: true };

  return (
      <>
        <div className="bg-white px-4 pb-1 mb-3 rounded-md">
          <ReportHeader title="Tab Management Report" />
          <ReportFilters
          includeSubEvents
              includeDates
              includeTime
              includeLocations
              includeVendors
              eventScopedVendors
              includeUsernames
              usernameOptions={usernameOptions}
              locationOptions={locationOptions}
              customQueryPaths={{
                dateFrom: "_and[0].transaction_time",
                dateTo: "_and[1].transaction_time",
              }}
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
            <div>
              <CustomSelect
                  options={tabStatusOptions}
                  defaultValue="all"
                  value={statusFilter}
                  onChange={(value) => setStatusFilter(value)}
                  title="Tab Status"
              />
            </div>
            <div></div>
            <div className="flex items-end justify-end">
              <SecondaryButton onClick={handleExportCSV}>Export to CSV</SecondaryButton>
            </div>
          </div>
        </div>

        <div style={{ height: "60vh" }}>
          <div className={"ag-theme-quartz"} style={{ width: "100%", height: "100%" }}>
            {isLoading ? (
                <Loader />
            ) : (
                <AgGridReact
                    rowData={data}
                    columnDefs={colDefs}
                    rowHeight={rowHeight}
                    autoSizeStrategy={autoSizeStrategy}
                    pagination={true}
                    gridOptions={gridOptions}
                    paginationPageSize={100}
                    onGridReady={(params) => (gridRef.current = params.api)}
                />
            )}
          </div>
        </div>
      </>
  );
};
